import type { GiteaHealth } from "./repositories";
import { request } from "./http";

export { BenchApiError } from "./http";

/**
 * Session and environment calls against the bench's own `/api` surface.
 *
 * The browser never talks to AMS directly: tokens, the `{data, meta}` envelope,
 * `Idempotency-Key` injection and async job polling all live server-side. Nothing in this file
 * has, or can obtain, an access token — the session is an HttpOnly cookie the page cannot read.
 */

/** One AMS deployment the bench can target. Mirrors `EnvironmentView` in `api_models.py`. */
export interface Environment {
  name: string;
  label: string;
  ams_base_url: string;
  auth_enabled: boolean;
  dev_token_expected: boolean;
  destructive_allowed: boolean;
  default_tenant: string;
  default_agent: string;
  default_projects: string[];
  /** Whether this environment can create and attach a repository. */
  can_provision_repositories: boolean;
  /** Whether the bench can find a repository's webhook secret itself, and so auto-sign. */
  can_discover_webhook_secrets: boolean;
  /** `dev-token`, `oidc` or `headers` — how this deployment is authenticated. */
  auth_mode: string;
  gitea_owner: string;
}

export interface EnvironmentListResponse {
  environments: Environment[];
  profiles: string[];
}

export interface BenchHealth {
  status: string;
  version: string;
  ui_built: boolean;
}

/** AMS scope headers the next call will carry. */
export interface Scope {
  tenant_id: string;
  project_id: string | null;
  agent_id: string | null;
  session_id: string | null;
}

/** Why a mint failed. Mirrors `MintFailure`; each value maps to distinct advice. */
export type MintFailure =
  | "route_absent"
  | "not_configured"
  | "keycloak_unreachable"
  | "unknown_profile"
  | "ams_unreachable"
  | "unexpected";

export interface GrantStatus {
  profile: string;
  available: boolean;
  seconds_remaining: number | null;
  failure: MintFailure | null;
  detail: string | null;
  /** Roles this identity's token carries, read from the token's own claims. */
  roles: string[];
  /** Shown instead of the profile name where there is a better one (the tester's own name). */
  label: string | null;
}

/**
 * Roles the chosen identity carries — the basis for saying which calls will be refused.
 *
 * Empty is a real answer, not a missing one: a token with no roles at all reaches 51 of the 55
 * operations, because AMS only positively requires a role in two places.
 */
export function selectedRoles(session: SessionView): string[] {
  return session.grants.find((grant) => grant.profile === session.profile)?.roles ?? [];
}

/** The signed-in person and what their token reaches. Absent in the dev-token flow. */
export interface TesterView {
  name: string;
  roles: string[];
}

export interface SessionView {
  environment: string;
  profile: string;
  scope: Scope;
  grants: GrantStatus[];
  authenticated: boolean;
  /** No grant at all — valid only where AMS runs with authentication disabled. */
  header_only: boolean;
  created_at: string;
  seconds_remaining: number | null;
  tester: TesterView | null;
}

export interface ReadyChecks {
  neo4j: boolean | null;
  redis: boolean | null;
  tei: boolean | null;
  neo4j_error: string | null;
  redis_error: string | null;
  tei_error: string | null;
}

export interface LlmDescriptor {
  provider: string | null;
  model: string | null;
  /** True when AMS falls back to the canned stub LLM — ask answers will not be real. */
  stub: boolean | null;
}

export interface AmsHealth {
  reachable: boolean;
  /** "ready" | "degraded" | "not_ready", straight from AMS. */
  status: string | null;
  http_status: number | null;
  checks: ReadyChecks | null;
  llm: LlmDescriptor | null;
  detail: string | null;
}

export interface EnvironmentHealth {
  environment: string;
  health: AmsHealth;
  /** Null when the environment has no Gitea configured, so there was nothing to probe. */
  gitea: GiteaHealth | null;
}

export interface ScopePatch {
  tenant_id?: string;
  project_id?: string;
  agent_id?: string;
  session_id?: string;
}

export function fetchEnvironments(signal?: AbortSignal): Promise<EnvironmentListResponse> {
  return request<EnvironmentListResponse>("/api/environments", { ...(signal ? { signal } : {}) });
}

export function fetchBenchHealth(signal?: AbortSignal): Promise<BenchHealth> {
  return request<BenchHealth>("/api/healthz", { ...(signal ? { signal } : {}) });
}

export function fetchEnvironmentHealth(
  name: string,
  signal?: AbortSignal,
): Promise<EnvironmentHealth> {
  return request<EnvironmentHealth>(`/api/environments/${encodeURIComponent(name)}/health`, {
    ...(signal ? { signal } : {}),
  });
}

export function fetchSession(signal?: AbortSignal): Promise<SessionView> {
  return request<SessionView>("/api/session", { ...(signal ? { signal } : {}) });
}

export function startSession(environment: string, profile: string): Promise<SessionView> {
  return request<SessionView>("/api/session", {
    method: "POST",
    body: { environment, profile },
  });
}

export function endSession(): Promise<void> {
  return request<void>("/api/session", { method: "DELETE" });
}

export function patchScope(patch: ScopePatch): Promise<SessionView> {
  return request<SessionView>("/api/session/scope", { method: "PATCH", body: patch });
}

export function patchProfile(profile: string): Promise<SessionView> {
  return request<SessionView>("/api/session/profile", { method: "PATCH", body: { profile } });
}

export function refreshSession(): Promise<SessionView> {
  return request<SessionView>("/api/session/refresh", { method: "POST" });
}

export function mintWorkingSessionId(): Promise<{ session_id: string }> {
  return request<{ session_id: string }>("/api/session/scope/new-session-id", { method: "POST" });
}
